import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CreditCard, AlertTriangle } from 'lucide-react';
import { buildUrl } from '../../config/api';
import '../styles/CardBypassStickyNote.css';

/**
 * CardBypassStickyNote
 *
 * Draggable sticky note that reminds the current user to add a credit card
 * for customers who have a Send Out appointment today but no card on file,
 * where the current user was the creator or updater of that appointment.
 *
 * Data comes from the server — both the original creator and any updater
 * (as stored in the EnteredBy field, e.g. "DidiP | Updated by: ShiraL")
 * will see the reminder when they log in.
 *
 * Listens to 'cardBypassReminder' to re-fetch immediately after a bypass.
 */
const CardBypassStickyNote = ({ user }) => {
  const [appointments, setAppointments] = useState([]);
  const [dismissed, setDismissed] = useState(new Set()); // dismissed IDs (session only)
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 400, y: window.innerHeight - 400 }); 
  const [dragging, setDragging] = useState(false);

  const navigate = useNavigate();
  const dragOffset = useRef({ x: 0, y: 0 });

  const username = user?.UserName || user?.username;

  const fetchReminders = useCallback(async () => {
    if (!username) return;
    try {
      const res = await fetch(
        buildUrl(`/api/appointments/no-card-reminders?username=${encodeURIComponent(username)}`)
      );
      if (!res.ok) return;
      const data = await res.json();
      const seen = new Set();
      const appts = (data.appointments || []).filter((a) => {
        if (seen.has(a.ClientID)) return false;
        seen.add(a.ClientID);
        return true;
      });
      setAppointments(appts);
      if (appts.length > 0) setVisible(true);
    } catch (err) {
      console.error('CardBypassStickyNote fetch error:', err);
    }
  }, [username]);

  // Fetch on mount
  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  // Re-fetch when a bypass just happened so the new appointment shows up immediately
  useEffect(() => {
    const onBypass = () => fetchReminders();
    window.addEventListener('cardBypassReminder', onBypass);
    return () => window.removeEventListener('cardBypassReminder', onBypass);
  }, [fetchReminders]);

  // Re-fetch when a card is saved — the server query will naturally drop that customer
  useEffect(() => {
    const onCardSaved = () => fetchReminders();
    window.addEventListener('cardSavedForCustomer', onCardSaved);
    return () => window.removeEventListener('cardSavedForCustomer', onCardSaved);
  }, [fetchReminders]);

  // Visible entries = fetched minus session-dismissed
  const visible_entries = appointments.filter((a) => !dismissed.has(a.ID));

  const dismissEntry = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // Hide the note when all entries are dismissed
  useEffect(() => {
    if (visible_entries.length === 0) setVisible(false);
  }, [visible_entries.length]);

  // Drag — mouse + touch events
  const startDrag = (clientX, clientY) => {
    setDragging(true);
    dragOffset.current = {
      x: clientX - position.x,
      y: clientY - position.y,
    };
  };

  const handleMouseDown = (e) => {
    if (!e.target.closest('.card-bypass-sticky-header')) return;
    e.preventDefault();
    startDrag(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    if (!e.target.closest('.card-bypass-sticky-header')) return;
    e.preventDefault();
    const t = e.touches[0];
    startDrag(t.clientX, t.clientY);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMouseMove = (e) =>
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    const onTouchMove = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      setPosition({
        x: t.clientX - dragOffset.current.x,
        y: t.clientY - dragOffset.current.y,
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging]);

  if (!visible || !username || visible_entries.length === 0) return null;

  const formatTime = (dt) => {
    if (!dt) return '';
    return new Date(dt).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  };

  return (
    <div
      className={`card-bypass-sticky${dragging ? ' dragging' : ''}`}
      style={{ left: position.x, top: position.y }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      {/* Header */}
      <div className="card-bypass-sticky-header">
        <div className="card-bypass-sticky-title">
          <AlertTriangle size={16} />
          Add Card Reminder
        </div>
        <button
          className="card-bypass-sticky-close"
          onClick={() => setVisible(false)}
          title="Hide for this session"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="card-bypass-sticky-body">
        <div className="card-bypass-sticky-subtitle">
          Send Outs — no card on file:
        </div>
        <div className="card-bypass-sticky-list">
          {visible_entries.map((appt) => (
            <div key={appt.ID} className="card-bypass-sticky-item">
              <div className="card-bypass-sticky-info">
                <div className="card-bypass-sticky-client">{appt.ClientName}</div>
                <div className="card-bypass-sticky-time">{formatTime(appt.EventStart)}</div>
              </div>
              <div className="card-bypass-sticky-actions">
                <button
                  className="card-bypass-sticky-go-btn"
                  onClick={() => navigate(`/customers/${appt.ClientID}`)}
                  title="Open customer profile to add card"
                >
                  <CreditCard size={13} />
                  + Card
                </button>
                <button
                  className="card-bypass-sticky-dismiss"
                  onClick={() => dismissEntry(appt.ID)}
                  title="Dismiss for this session"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CardBypassStickyNote;
